import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

import { Layout } from '../components/layout/Layout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { customerAPI } from '../services/ApiServices';
import placeholder from '../assets/avatar-placeholder.png';

/* =========================
   FIELD & MISMATCH HELPERS
========================= */

const FIELD_MAP = {
  'Full Name': 'fullName',
  'First Name': 'givenName',
  'Last Name': 'familyName',
  'Date of Birth': 'dateOfBirth',
  'Gender': 'gender',
  'Phone Number': 'phoneNumber',
  'Email': 'email',
  'Nationality': 'nationality',
};

const ADDRESS_FIELD_MAP = {
  woreda: ['Woreda'],
  zone: ['Zone'],
  region: ['Region'],
};

const buildAddressMismatchLookup = (mismatches = []) => {
  const lookup = {};

  mismatches.forEach((m) => {
    Object.entries(ADDRESS_FIELD_MAP).forEach(([key, labels]) => {
      if (labels.includes(m.field)) {
        lookup[key] = m;
      }
    });
  });

  return lookup;
};


const buildMismatchLookup = (mismatches = []) =>
  mismatches.reduce((acc, m) => {
    const key = FIELD_MAP[m.field];
    if (key) acc[key] = m;
    return acc;
  }, {});

const tooltip = (m) =>
  `OFS: ${m.ofsValue || '-'} | Fayda: ${m.faydaValue || '-'}`;

/* =========================
   FIELD ROW
========================= */

const FieldRow = ({ label, value, mismatch, theme }) => {
  const isMismatch = !!mismatch;

  return (
    <div
      className={`grid grid-cols-3 gap-4 py-2 border-b last:border-0 ${theme === 'ofs' ? 'border-blue-100' : 'border-green-100'
        }`}
      title={isMismatch ? tooltip(mismatch) : ''}
    >
      <div className="col-span-1 flex items-center gap-1">
        {isMismatch && <span className="w-2 h-2 rounded-full bg-red-500" />}
        <p
          className={`text-xs font-semibold ${isMismatch
            ? 'text-red-700'
            : theme === 'ofs'
              ? 'text-blue-700'
              : 'text-green-700'
            }`}
        >
          {label}
        </p>
      </div>

      <div className="col-span-2">
        <p className={`text-sm font-medium ${isMismatch ? 'text-red-600' : 'text-gray-900'}`}>
          {value || '-'}
        </p>
      </div>
    </div>
  );
};

/* =========================
   DATA SECTION
========================= */

const DataSection = ({ ofsData, faydaData, mismatches }) => {
  const mismatchLookup = buildMismatchLookup(mismatches);
  const addressMismatchLookup = buildAddressMismatchLookup(mismatches);

  const fields = [
    { key: 'fullName', label: 'Full Name' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'gender', label: 'Gender' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'email', label: 'Email' },
    { key: 'nationality', label: 'Nationality' },
  ];

  return (
    <Card className="mb-6">
      <h3 className="text-lg font-semibold mb-4 pb-3 border-b">
        Customer Verification Data
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <h4 className="text-sm font-semibold text-blue-800 mb-4">
            System Data (CBS)
          </h4>

          {ofsData?.picture ? (
            <div className="flex justify-center mb-4">
              <img src={ofsData.picture} alt="OFS" className="w-32 h-32 rounded-full object-cover" />
            </div>
          ) : (
            <div className="flex justify-center mb-4">
              <img src={placeholder} alt="OFS" className="w-32 h-32 rounded-full object-cover" />
            </div>)}
          {fields.map((f) => (
            <FieldRow
              key={f.key}
              label={f.label}
              value={ofsData[f.key]}
              mismatch={mismatchLookup[f.key]}
              theme="ofs"
            />
          ))}


          {ofsData?.address &&
            Object.entries(ofsData.address).map(([k, v]) => {
              const mismatch = addressMismatchLookup[k];
              const isMismatch = !!mismatch;

              return (
                <div
                  key={k}
                  className={`grid grid-cols-3 gap-4 py-1 border-b last:border-0 ${isMismatch ? 'bg-red-50' : ''
                    }`}
                  title={isMismatch ? tooltip(mismatch) : ''}
                >
                  <div className="col-span-1 flex items-center gap-1">
                    {isMismatch && <span className="w-2 h-2 rounded-full bg-red-500" />}
                    <p className={`text-xs font-semibold ${isMismatch ? 'text-red-700' : 'text-blue-600'
                      }`}>
                      {k}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className={`text-sm ${isMismatch ? 'text-red-600' : 'text-gray-800'
                      }`}>
                      {v || '-'}
                    </p>
                  </div>
                </div>
              );
            })}

        </Card>

        <Card className="border-2 border-green-200 bg-green-50/50">
          <h4 className="text-sm font-semibold text-green-800 mb-4">
            Fayda API Data
          </h4>

          {faydaData?.picture && (
            <div className="flex justify-center mb-4">
              <img src={faydaData.picture} alt="Fayda" className="w-32 h-32 rounded-full object-cover" />
            </div>
          )}

          {fields.map((f) => (
            <FieldRow
              key={f.key}
              label={f.label}
              value={faydaData[f.key]}
              mismatch={mismatchLookup[f.key]}
              theme="fayda"
            />
          ))}

          {faydaData?.address &&
            Object.entries(faydaData.address).map(([k, v]) => {
              const mismatch = addressMismatchLookup[k];
              const isMismatch = !!mismatch;

              return (
                <div
                  key={k}
                  className={`grid grid-cols-3 gap-4 py-1 border-b last:border-0 ${isMismatch ? 'bg-red-50' : ''
                    }`}
                  title={isMismatch ? tooltip(mismatch) : ''}
                >
                  <div className="col-span-1 flex items-center gap-1">
                    {isMismatch && <span className="w-2 h-2 rounded-full bg-red-500" />}
                    <p className={`text-xs font-semibold ${isMismatch ? 'text-red-700' : 'text-blue-600'
                      }`}>
                      {k}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className={`text-sm ${isMismatch ? 'text-red-600' : 'text-gray-800'
                      }`}>
                      {v || '-'}
                    </p>
                  </div>
                </div>
              );
            })}
        </Card>
      </div>
    </Card>
  );
};

/* =========================
   MAIN SCREEN
========================= */

export default function CustomerVerification() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showOverrideReasonModal, setShowOverrideReasonModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');


  // ✅ READ CUSTOMER FROM ROUTER STATE
  const customer = location.state?.customer;
  const customerId = customer?.ofsData?.customerId;
  console.log('CustomerVerification customer:', customer);

  // ❗ Safety fallback (refresh / direct URL access)
  if (!customer) {
    navigate('/pending');
    return null;
  }


  const handleApprove = async (approvalType) => {
    setIsLoading(true);
    setShowSuccessModal(false);
    setShowErrorModal(false);
    setErrorMessage('');
    setSuccessMessage('');
    setShowOverrideReasonModal(false);
    setOverrideReason('');


    try {
      const payload = {
        action: 'approve',
        comment: 'All data verified - approved',
        approvalType,
      };

      // if (approvalType === 'APPROVE_AND_OVERRIDE') {
      //   if (!overrideReason.trim()) {
      //     alert('Override reason is required');
      //     setIsLoading(false);
      //     return;
      //   }

      //   payload.reason = overrideReason;
      // }

      await customerAPI.verifyCustomer(customerId, payload);

      // ✅ CLOSE MODALS

      setSuccessMessage(
        approvalType === 'APPROVE_AND_OVERRIDE'
          ? 'Customer approved with override successfully'
          : 'Customer approved successfully'
      );

      setShowSuccessModal(true);

    } catch (err) {
      setErrorMessage(
        typeof err === 'string'
          ? err
          : err?.message || 'Operation failed. Please try again.'
      );
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };



  const handleModalConfirm = () => {
    setShowSuccessModal(false);
    navigate('/pending');
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Rejection reason required');
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        action: 'reject',
        comment: 'Data mismatch - ' + rejectionReason,
        approvalType: null,
      };

      await customerAPI.verifyCustomer(customerId, payload);

      // ✅ CLOSE REJECT MODAL
      setShowRejectModal(false);
      setRejectionReason('');

      setSuccessMessage('Customer rejected successfully');
      setShowSuccessModal(true);

    } catch (err) {
      setErrorMessage(
        err?.message || 'Customer rejection failed. Please try again.'
      );
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };


  const ofsData = {
    fullName: customer.ofsData.fullName,
    dateOfBirth: customer.ofsData.dateOfBirth,
    gender: customer.ofsData.gender,
    phoneNumber: customer.ofsData.phone,
    email: customer.ofsData.email,
    nationality: customer.ofsData.nationality,
    address: customer.ofsData.address,
    picture: customer.ofsData.image,
    companyCode: customer.ofsData.companyCode,
  };

  const faydaData = {
    fullName: customer.faydaData.name,
    dateOfBirth: customer.faydaData.birthdate,
    gender: customer.faydaData.gender,
    phoneNumber: customer.faydaData.phoneNumber,
    email: customer.faydaData.email,
    picture: customer.faydaData.picture,
    nationality: customer.faydaData.nationality,
    address: customer.faydaData.address,
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="modal bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <div className="modal-header mb-4">
                <h3 className="text-xl font-semibold text-green-600">Success!</h3>
              </div>
              <div className="modal-body mb-6">
                <p className="text-gray-700">{successMessage}</p>
              </div>
              <div className="modal-footer flex justify-end">
                <button
                  onClick={handleModalConfirm}
                  className="gap-2 w-10 rounded-sm rounded bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="modal bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
              <div className="modal-header mb-4">
                <h3 className="text-xl font-semibold text-red-600">Error!</h3>
              </div>
              <div className="modal-body mb-6">
                <p className="text-gray-700">{errorMessage}</p>
              </div>
              <div className="modal-footer flex justify-end">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="gap-2 w-10 rounded-sm rounded bg-gradient-to-r from-[#472f92] to-[#e4aa24] text-white"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HEADER */}
        <Button variant="ghost" onClick={() => navigate('/pending')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <h1 className="text-3xl font-bold mb-2">
          {customer.ofsData.fullName}{' ('}{customer.ofsData.companyCode}{')'}
        </h1>

        {/* INFO BANNER */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
          <AlertCircle className="text-blue-600 w-5 h-5 mt-1" />
          <p className="text-sm">
            {customer.mismatchedFields.length > 0 ? (
              <span className="font-semibold text-red-600">
                {customer.mismatchedFields.length} mismatches found
              </span>
            ) : (
              <span className="font-semibold text-green-600">
                All data matches
              </span>
            )}
          </p>
        </div>

        <DataSection
          ofsData={ofsData}
          faydaData={faydaData}
          mismatches={customer.mismatchedFields}
        />

        {/* ACTIONS */}
        <Card className="mt-6 flex justify-end gap-3">
          <Button variant="danger" onClick={() => setShowRejectModal(true)}>
            <XCircle className="w-4 h-4 mr-2" />
            Reject
          </Button>
          <Button className="!bg-[#e4aa24]" onClick={() => handleApprove('APPROVE_ONLY')} isLoading={submitting}>
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve
          </Button>
          <Button className='!bg-[#472f92] hover:!bg-[#3b267c]' onClick={() => { 
            // setShowOverrideReasonModal(true);
             handleApprove('APPROVE_AND_OVERRIDE');
            }} isLoading={submitting}>
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve & override
          </Button>
        </Card>

        {/* REJECT MODAL */}
        <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)}>
          <input
            className="w-full border p-3 rounded"
            placeholder="Rejection reason"
            value={rejectionReason}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only letters and spaces
              if (/^[A-Za-z\s]*$/.test(value)) {
                setRejectionReason(value);
              }
            }}
          />
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="ghost" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleReject}>
              Confirm Reject
            </Button>
          </div>
        </Modal>

        {/* Overide MODAL */}
        <Modal isOpen={showOverrideReasonModal} onClose={() => setShowOverrideReasonModal(false)}>
          <input
            className="w-full border p-3 rounded"
            placeholder="Override reason"
            value={overrideReason}
            onChange={(e) => {
              const value = e.target.value;
              // Allow only letters and spaces
              if (/^[A-Za-z\s]*$/.test(value)) {
                setOverrideReason(e.target.value);
              }
            }}
          />
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="ghost" onClick={() => setShowOverrideReasonModal(false)}>
              Cancel
            </Button>
            <Button className='!bg-[#472f92] hover:!bg-[#3b267c]' onClick={() => { handleApprove('APPROVE_AND_OVERRIDE') }}>
              Confirm Override
            </Button>
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
